/*
| ======================================
| This File is for tracking / gtm helper
| ======================================
*/

import { Capacitor } from '@capacitor/core'
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics'
import { CONFIG } from '~/services/config'

// Interactions whose entered value may ride the `User Input` event (HI-1490).
// This is an ALLOW-list, not a deny-list, so it fails closed: a newly
// instrumented field is stripped by default rather than silently shipping what
// the user typed. Anything absent from this list — name, email, phone, date of
// birth — fires with its internalTrackingId and non-value properties only, and the
// `value` key is OMITTED, never blanked or masked. A blank would be
// indistinguishable from "the user typed nothing" downstream.
// See agent-os/standards/tracking/tracking.md (Privacy + PII).
const VALUE_SAFE_TRACKING_IDS = new Set([
  'input_hi_step1_postcode',
  'input_hi_step3_current_fund',
  // HI-1430 Energy SQPP: postcode digits and retailer names only. The moving
  // date, address, business name and contact fields stay stripped.
  'input_en_postcode',
  'input_en_electricity_retailer',
  'input_en_gas_retailer'
])

export default defineNuxtPlugin({
  name: 'tracking',
  dependsOn: ['app-device-info'],
  setup () {
    const userStore = useUserStore()
    const { environment } = useRuntimeConfig().public

    // In the Capacitor app the WebView origin is https://localhost (Android) or
    // capacitor://localhost (iOS), which would attribute all GA/GTM hits to
    // "localhost". Map it to the real per-env web origin so analytics reports
    // proper hostname + page_location. (Web builds keep the live URL untouched.)
    const canonicalOrigin = Capacitor.isNativePlatform()
      ? CONFIG.baseUrl(environment, 'web')
      : null
    class Tracking {
      constructor () {
        this.additionalPagePayload = {}
        const { $appDeviceInfo } = useNuxtApp()
        this.deviceInfo = $appDeviceInfo || {}
      }

      updatePayload () {
        const pathName = window.location.pathname
        const ogType = document.querySelector('meta[property="og:type"]')

        this.dataLayer = window.dataLayer || []

        // Use the real web origin for native app builds so GA/GTM don't attribute
        // hits to localhost. Map page_location/page_hostname to these dataLayer
        // values in the GTM container's GA4 config tag.
        const search = window.location.search
        const hash = window.location.hash
        let fullUrl = window.location.href
        let hostname = window.location.hostname
        if (canonicalOrigin) {
          fullUrl = `${canonicalOrigin}${pathName}${search}${hash}`
          try {
            hostname = new URL(canonicalOrigin).hostname
          } catch {
            // keep window hostname on parse failure
          }
        }

        this.page = {
          fullUrl,
          fullPath: `${pathName}${search}`,
          path: pathName,
          hostname,
          title: document.title,
          pageType: ogType ? ogType.content : '',
          displayMode: this.deviceInfo.displayMode || 'browser'
        }

        // Add the user's email hash if available
        if (userStore.isUserLoggedIn) {
          this.additionalParams = {
            ...this.additionalParams,
            clubplus_user: { emailHashed: userStore.getUserProfile.hash_id }
          }
        }
      }

      pageview (payload = {}) {
        this.event = 'pageview'
        this.additionalParams = {}
        this.additionalPagePayload = payload
        this.execute()
      }

      click (eventElement, eventTargetUrl = '', params) {
        const eventElementPath = eventElement.path ? eventElement.path : [eventElement.target]
        const findButtonHref = eventElementPath.filter(d => {
          return (d.tagName === 'BUTTON' || d.tagName === 'A' || d.tagName === 'a') && d.getAttribute('id')
        })

        let elementUrl = eventTargetUrl ?? (findButtonHref.length < 1 ? '' : findButtonHref[0].getAttribute('href') ? findButtonHref[0].getAttribute('href') : eventTargetUrl || window.location.href)
        if (!elementUrl.includes('http')) {
          elementUrl = window.location.origin + elementUrl
        }

        this.additionalParams = {
          elementId: findButtonHref.length > 0 ? findButtonHref[0].id : '',
          elementInnerText: findButtonHref.length < 1 ? '' : findButtonHref[0].innerText !== '' ? findButtonHref[0].innerText : findButtonHref[0].getAttribute('data-innertext'),
          elementButtonUrl: elementUrl,
          ...params
        }

        this.event = 'Button Clicked'
        this.execute()
      }

      // Rule 1 (HI-1490): the general button event, for elements that do NOT
      // already fire it — selections (rendered as <li>), plain links, and
      // buttons carrying `:enable-tracking="false"`.
      //
      // Anything rendered by V2AtomsButton with tracking enabled already pushes
      // `Button Clicked` from handleClick, so those pass `internalTrackingId` through
      // `trackingAdditionalParams` instead. Calling this for them would emit a
      // second `Button Clicked` and double-count the interaction.
      //
      // `click()` derives the element* keys by walking the event path for a
      // <button>/<a> that carries an id, which never matches an <li>. Set them
      // explicitly here rather than inheriting empty / origin-only values by
      // accident. `internalTrackingId` goes last so a caller cannot clobber it.
      trackedClick (internalTrackingId, params = {}) {
        this.additionalParams = {
          elementId: '',
          elementInnerText: '',
          elementButtonUrl: window.location.href,
          ...params,
          internalTrackingId
        }

        this.event = 'Button Clicked'
        this.execute()
      }

      // Rules 2 + 3 (HI-1490): the general input event. Fired on BLUR only —
      // never on `update` / `update:modelValue`, which AtomsInput emits on every
      // keystroke. `value` is dropped unless the internalTrackingId is allow-listed
      // above; the key is left off the payload entirely rather than emptied.
      // HI-1568: `eventName` lets a caller land on a different event (e.g. the
      // dedicated `User Input Focus`) while reusing the same PII-stripping logic.
      userInput (internalTrackingId, params = {}, eventName = 'User Input') {
        const { value, ...rest } = params
        const payload = { ...rest, internalTrackingId }

        if (value !== undefined && VALUE_SAFE_TRACKING_IDS.has(internalTrackingId)) {
          payload.value = value
        }

        this.additionalParams = payload
        this.event = eventName
        this.execute()
      }

      login (params) {
        this.event = 'My Account Log In'
        this.additionalParams = params
        this.execute()
      }

      gsdCallback (params) {
        this.event = 'Callback Requested'
        this.additionalParams = params
        this.execute()
      }

      personalDetails (vertical, params) {
        this.event = vertical + ' Quote - Personal Details Added'
        this.additionalParams = params
        this.execute()
      }

      customEvent (event, params) {
        this.event = event
        this.additionalParams = params
        this.execute()
      }

      // Henry AI Advisor (OTTO) chat funnel: the chat_* events of the Henry AI
      // Advisor Energy Tracking plan. otto-ui sends them through the OttoHost
      // `track` capability (app/otto/nuxtHost.ts) already carrying vertical, agent
      // and conversation_id, and never PII; the channel is this app's to add, and
      // goes last so a caller cannot clobber it.
      chat (event, params = {}) {
        this.event = event
        this.additionalParams = { ...params, channel: 'clubplus' }
        this.execute()
      }

      push () {
        this.updatePayload()
        const payload = {
          event: this.event,
          page: {
            ...this.page,
            ...this.additionalPagePayload
          },
          device: {
            platform: this.deviceInfo.platform || '',
            displayMode: this.deviceInfo.displayMode || '',
            appVersion: this.deviceInfo.appVersion || '',
            appBuild: this.deviceInfo.appBuild || '',
            osVersion: this.deviceInfo.osVersion || '',
            deviceModel: this.deviceInfo.deviceModel || '',
            manufacturer: this.deviceInfo.manufacturer || '',
            pushPermission: this.deviceInfo.pushPermission || ''
          },
          ...this.additionalParams
        }
        this.dataLayer.push(payload)

        // Forward to native Firebase Analytics on Capacitor builds
        if (Capacitor.isNativePlatform()) {
          try {
            // Build Firebase params from the full payload
            const firebaseParams = {}

            // Flatten page attributes
            if (payload.page) {
              firebaseParams.page_full_url = payload.page.fullUrl || ''
              firebaseParams.page_full_path = payload.page.fullPath || ''
              firebaseParams.page_path = payload.page.path || ''
              firebaseParams.page_title = payload.page.title || ''
              firebaseParams.page_type = payload.page.pageType || ''
              firebaseParams.display_mode = payload.page.displayMode || ''
            }

            // Include device attributes
            if (payload.device) {
              firebaseParams.device_platform = payload.device.platform || ''
              firebaseParams.device_display_mode = payload.device.displayMode || ''
              firebaseParams.device_app_version = payload.device.appVersion || ''
              firebaseParams.device_os_version = payload.device.osVersion || ''
              firebaseParams.device_model = payload.device.deviceModel || ''
              firebaseParams.device_manufacturer = payload.device.manufacturer || ''
              firebaseParams.device_push_permission = payload.device.pushPermission || ''
            }

            // Include clubplus_user attributes
            if (payload.clubplus_user) {
              firebaseParams.email_hashed = payload.clubplus_user.emailHashed || ''
            }

            // Include any additional params (excluding already-handled keys)
            const excludeKeys = ['event', 'page', 'clubplus_user', 'gtm.uniqueEventId']
            Object.keys(payload).forEach(key => {
              if (!excludeKeys.includes(key)) {
                const val = payload[key]
                firebaseParams[key.replace(/[^a-zA-Z0-9_]/g, '_')] = typeof val === 'object' ? JSON.stringify(val) : String(val)
              }
            })

            // Truncate param values to Firebase's 100-char limit
            Object.keys(firebaseParams).forEach(key => {
              if (typeof firebaseParams[key] === 'string' && firebaseParams[key].length > 100) {
                firebaseParams[key] = firebaseParams[key].substring(0, 100)
              }
            })

            if (this.event === 'pageview') {
              // Set screen name for screen_view tracking
              FirebaseAnalytics.setScreenName({
                screenName: payload.page.fullPath || payload.page.path || 'Unknown',
                nameOverride: payload.page.title || 'WebPage'
              })

              // Also log as a custom event so all attributes are captured
              FirebaseAnalytics.logEvent({
                name: 'pageview',
                params: firebaseParams
              })
            } else {
              FirebaseAnalytics.logEvent({
                name: this.event.replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 40),
                params: firebaseParams
              })
            }
          } catch (e) {
            throw new Error('Firebase Analytics error', { cause: e })
          }
        }
      }

      execute () {
        if (this.event === 'pageview') {
          // There is a known bug around page title. delaying the push by .5 second
          // https://github.com/nuxt/vue-meta/issues/567
          // Push from a snapshot of what this pageview set: an event fired during
          // the delay (the chat_menu click that started this navigation, say)
          // reuses this instance and would otherwise be pushed twice in the
          // pageview's place. The instance's own state is put back after the push,
          // so later events keep the latest page context rather than this older
          // one. The page itself, title included, is still read when the push runs.
          const queued = { event: this.event, additionalParams: this.additionalParams, additionalPagePayload: this.additionalPagePayload }
          setTimeout(() => {
            const current = { event: this.event, additionalParams: this.additionalParams, additionalPagePayload: this.additionalPagePayload }
            Object.assign(this, queued)
            try {
              this.push()
            } finally {
              Object.assign(this, current)
            }
          }, 500)
          return
        }
        this.push()
      }

      // Kept for backwards compatibility — external callers use this method directly.
      // Delegates to $appDeviceInfo.displayMode which uses reliable persisted detection.
      getPWADisplayMode () {
        return this.deviceInfo.displayMode || 'browser'
      }
    }

    return {
      provide: {
        tracking: new Tracking()
      }
    }
  }
})
